/**
 * FcmPermissionBanner — F-B.6
 *
 * Browser-Push-Benachrichtigungen beantragen und FCM-Token an Backend senden.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const DISMISSED_KEY = 'fcm-banner-dismissed'

type Props = {
  isMutating: boolean
  onSave: (payload: { fcm_token: string; fcm_enabled: boolean }) => Promise<void>
}

export function FcmPermissionBanner({ isMutating, onSave }: Props) {
  const { t } = useTranslation('settings')
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  )
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) === '1')
  const [requesting, setRequesting] = useState(false)

  if (typeof Notification === 'undefined') return null
  if (dismissed && permission === 'default') return null

  async function handleAllow() {
    setRequesting(true)
    try {
      const result = await Notification.requestPermission()
      setPermission(result)
      if (result === 'granted') {
        // In echtem Projekt: firebase.getToken() aufrufen
        // Hier senden wir einen Platzhalter-Token bis Firebase konfiguriert ist
        await onSave({ fcm_token: 'browser-push-enabled', fcm_enabled: true })
      }
    } finally {
      setRequesting(false)
    }
  }

  function handleDismiss() {
    localStorage.setItem(DISMISSED_KEY, '1')
    setDismissed(true)
  }

  return (
    <div className="card card-pad" style={{ marginBottom: 18 }} data-testid="fcm-permission-banner">
      <h3 style={{ marginBottom: 14 }}>{t('fcm.title')}</h3>

      {permission === 'granted' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="pill pill-primary">{t('fcm.activated')}</span>
          <span className="muted" style={{ fontSize: 13 }}>
            {t('fcm.activatedHint')}
          </span>
        </div>
      ) : permission === 'denied' ? (
        <div>
          <p className="muted" style={{ fontSize: 13 }}>
            {t('fcm.denied')}
          </p>
        </div>
      ) : (
        <div>
          <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            {t('fcm.prompt')}
          </p>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleAllow}
              disabled={requesting || isMutating}
              data-testid="fcm-allow-btn"
            >
              {requesting ? t('fcm.allowing') : t('fcm.allow')}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleDismiss}
              data-testid="fcm-dismiss-btn"
            >
              {t('fcm.dismiss')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
